import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, Phone } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8daf730c-66f1-48f9-9ec4-f1965e82cf39/artifacts/8s88x8hu_logo_officiel-removebg-preview%20%281%29.png";

const navLinks = [
  { name: "Accueil", path: "/" },
  { name: "Nos Services", path: "/services" },
  { name: "Réalisations", path: "/realisations" },
  { name: "À Propos", path: "/a-propos" },
  { name: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-md py-2" : "bg-white/95 py-4"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Qualidéco"
              className="h-14 md:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link font-medium text-[#333333] hover:text-[#E74C3C] transition-colors ${
                  location.pathname === link.path ? "text-[#E74C3C] active" : ""
                }`}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:0385392938"
              className="flex items-center gap-2 text-[#333333] hover:text-[#1B93A4] transition-colors"
              data-testid="phone-link"
            >
              <Phone size={20} weight="bold" />
              <span className="font-medium">03 85 39 29 38</span>
            </a>
            <Link to="/contact">
              <Button
                className="bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide px-6 py-2 rounded-sm transition-all hover:-translate-y-0.5"
                data-testid="cta-devis-btn"
              >
                Demander un Devis
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#333333]"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Fermer le menu" : "Ouvrir le menu"}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={28} weight="bold" /> : <List size={28} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`py-3 px-4 font-medium rounded-sm transition-colors ${
                    location.pathname === link.path
                      ? "bg-[#F8F9FA] text-[#E74C3C]"
                      : "text-[#333333] hover:bg-[#F8F9FA]"
                  }`}
                  data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-100 mt-2">
                <a
                  href="tel:0385392938"
                  className="flex items-center gap-2 py-3 px-4 text-[#1B93A4] font-medium"
                  data-testid="mobile-phone-link"
                >
                  <Phone size={20} weight="bold" />
                  <span>03 85 39 29 38</span>
                </a>
                <Link to="/contact" className="block mt-2">
                  <Button
                    className="w-full bg-[#E74C3C] hover:bg-[#C0392B] text-white font-bold uppercase tracking-wide py-3 rounded-sm"
                    data-testid="mobile-cta-devis-btn"
                  >
                    Demander un Devis
                  </Button>
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
